import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { CoachInterface } from './components/coach/CoachInterface';
import { LoginForm } from './components/auth/LoginForm';
import { DashboardPage } from './pages/DashboardPage';
import { NutritionOverviewPage } from './pages/NutritionOverviewPage';
import { GoalsProgressPage } from './pages/GoalsProgressPage';
import { ReportsPage } from './pages/ReportsPage';
import { ProfilePage } from './pages/ProfilePage';
import { ClientActivityPage } from './pages/ClientActivityPage';
import { MealPlanningPage } from './pages/MealPlanningPage';
import { apiClient } from './config/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<any>(null);
  const [isCoach, setIsCoach] = useState<boolean>(false);

  useEffect(() => {
    // Bootstrap session: works for both legacy localStorage tokens AND the
    // HttpOnly cookie set by /api/external/ankorium/sso. We probe /auth/me;
    // if the server returns a user, we're signed in regardless of which
    // mechanism actually carried the credential.
    const bootstrap = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) apiClient.setToken(token);
      try {
        const res = await apiClient.getMe();
        if (res?.user) {
          setUser(res.user);
          setIsAuthenticated(true);
          await checkIfUserIsCoach();
        }
      } catch {
        // No active session — fall through to login screen.
      }
      setIsLoading(false);
    };
    bootstrap();
  }, []);

  const checkIfUserIsCoach = async () => {
    try {
      // Try to fetch coach clients - if successful, user is a coach
      await apiClient.getCoachClients();
      setIsCoach(true);
    } catch (error) {
      // If it fails, user is not a coach
      setIsCoach(false);
    }
  };

  const handleLogin = async (email: string, password: string) => {
    try {
      const response = await apiClient.login(email, password) as any;

      // Handle cookie-based authentication (no token in response)
      if (response.success || response.user) {
        // Token is in HTTP-only cookie, no need to store it
        setUser(response.user);
        setIsAuthenticated(true);
        await checkIfUserIsCoach();
      } else {
        // Fallback to token-based authentication
        const token = response.tokens?.accessToken || response.token;
        if (token) {
          apiClient.setToken(token);
          setUser(response.user);
          setIsAuthenticated(true);
          await checkIfUserIsCoach();
        } else {
          throw new Error('Login response missing user or token');
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    apiClient.clearToken();
    setUser(null);
    setIsAuthenticated(false);
    setIsCoach(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-gray-50">
          {!isAuthenticated ? (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
              <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                  <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                    NutriSnap Professional
                  </h2>
                  <p className="mt-2 text-sm text-gray-600">
                    Comprehensive Nutrition Dashboard
                  </p>
                </div>
                <LoginForm onLogin={handleLogin} />
              </div>
            </div>
          ) : isCoach ? (
            // Coach Interface - Top Level
            <CoachInterface user={user} onLogout={handleLogout} />
          ) : (
            // Regular User Dashboard  
            <DashboardLayout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="/" element={<DashboardPage user={user} />} />
                <Route path="/nutrition-overview" element={<NutritionOverviewPage user={user} />} />
                <Route path="/goals-progress" element={<GoalsProgressPage user={user} />} />
                <Route path="/meal-planning" element={<MealPlanningPage user={user} />} />
                <Route path="/reports" element={<ReportsPage user={user} />} />
                <Route path="/client-activity" element={<ClientActivityPage user={user} />} />
                <Route path="/profile" element={<ProfilePage user={user} />} />
              </Routes>
            </DashboardLayout>
          )}
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
